/**
 * Shared Price / Payment Amount formatting for Payment Breakdown grids
 * (Lookup Personal tab + My Application / EP dashboard).
 */

export function parseMoneyToNumber(v: unknown): number {
  if (v == null || v === '') return NaN;
  const s = String(v).replace(/[$,()\s]/g, '');
  const n = parseFloat(s);
  return n;
}

/** Whole-dollar USD: `$0`, `$25` — no `.00`. */
export function formatUsdWhole(value: unknown): string {
  const n = parseMoneyToNumber(value);
  if (!Number.isFinite(n)) {
    return value == null || value === '' ? '' : String(value);
  }
  return `$${Math.round(Math.abs(n))}`;
}

function revenueKey(row: Record<string, unknown>): string {
  const raw =
    row['cd_revenue_code'] ??
    row['cdRevenueCode'] ??
    row['feeCategoryKey'] ??
    row['strCdRevenueCodeKey'] ??
    row['cd_revenue_code_key'] ??
    '';
  return String(raw ?? '').trim();
}

function revenueCode(row: Record<string, unknown>): string {
  const raw = row['revenue_code'] ?? row['revenueCode'] ?? row['cd_revenue_code'] ?? '';
  return String(raw ?? '').trim().toUpperCase();
}

function isTruthyFlag(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}

export function isMilitaryExemptionRow(row: Record<string, unknown>): boolean {
  if (isTruthyFlag(row['is_discount']) || isTruthyFlag(row['is_exemption'])) {
    return true;
  }
  const key = revenueKey(row);
  if (['5', '9'].includes(key)) return true;
  const code = revenueCode(row);
  return code.startsWith('RC-MIL');
}

export function isCreditUtilRow(row: Record<string, unknown>): boolean {
  if (!isTruthyFlag(row['is_credit'])) return false;
  const key = revenueKey(row);
  if (key === '10') return true;
  const code = revenueCode(row);
  if (code === 'RC-CREDIT') return true;
  const desc = String(
    row['revenue_code_desc'] ?? row['revenueCodeDesc'] ?? row['revenue_code_description'] ?? '',
  )
    .trim()
    .toLowerCase();
  return desc.includes('educator credit') || desc.includes('credit (util)') || desc.includes('credit applied');
}

export function isCreditAddRow(row: Record<string, unknown>): boolean {
  return isTruthyFlag(row['is_credit']) && !isCreditUtilRow(row);
}

export function isRefundRow(row: Record<string, unknown>): boolean {
  return isTruthyFlag(row['is_refund']);
}

function unitPriceRaw(row: Record<string, unknown>): unknown {
  return (
    row['price'] ??
    row['strPrice'] ??
    row['original_amount'] ??
    row['strOriginalAmount'] ??
    row['originalAmount'] ??
    0
  );
}

function paidAmountRaw(row: Record<string, unknown>): unknown {
  return (
    row['payment_amount'] ??
    row['strPaymentAmount'] ??
    row['paid_amount'] ??
    row['strPaidAmount'] ??
    row['amount'] ??
    row['paidAmount'] ??
    0
  );
}

/**
 * Price column: never parentheses.
 * Credit rows → `$0`; others → unit price from DB.
 */
export function formatPaymentBreakdownPrice(row: Record<string, unknown>): string {
  if (isCreditUtilRow(row) || isCreditAddRow(row)) {
    return formatUsdWhole(0);
  }
  return formatUsdWhole(unitPriceRaw(row));
}

/**
 * Payment Amount column with row-type bracket rules.
 */
export function formatPaymentBreakdownAmount(row: Record<string, unknown>): string {
  if (isCreditUtilRow(row)) {
    const n = parseMoneyToNumber(unitPriceRaw(row));
    const fallback = parseMoneyToNumber(paidAmountRaw(row));
    const value = Number.isFinite(n) && n !== 0 ? n : fallback;
    return `(${formatUsdWhole(value)})`;
  }

  if (isCreditAddRow(row)) {
    const paid = parseMoneyToNumber(paidAmountRaw(row));
    const unit = parseMoneyToNumber(unitPriceRaw(row));
    const value = Number.isFinite(paid) && paid > 0 ? paid : unit;
    return formatUsdWhole(value);
  }

  if (isMilitaryExemptionRow(row)) {
    const unit = parseMoneyToNumber(unitPriceRaw(row));
    const paid = parseMoneyToNumber(paidAmountRaw(row));
    const value = Number.isFinite(unit) && unit !== 0 ? unit : paid;
    return `(${formatUsdWhole(value)})`;
  }

  if (isRefundRow(row)) {
    const paid = parseMoneyToNumber(paidAmountRaw(row));
    const unit = parseMoneyToNumber(unitPriceRaw(row));
    // Manual refunds store value in original_amount with paid_amount = 0.
    const value = Number.isFinite(paid) && paid > 0 ? paid : unit;
    return formatUsdWhole(value);
  }

  return formatUsdWhole(paidAmountRaw(row));
}
